// Pullman Reports → Pullman People (2026-10-23, Kathryn Watson plan item 4).
// Every service in the picked range with the people travelling, a click-through to the
// carriage and table layout (the Pullman Offline geometry, app.PullmanCarriageLayout),
// a week / month / season panel, the food summary counted once per booking, an Update
// now button (one an hour, shared), XLS + PDF downloads, and Compare to last season.
// Data: /api/dashboards/pullman/people (api/src/functions/pullmanPeople.ts).
import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import { LY_GREY, deltaLabel, shortSeasonLabel, windowLabel, type CompareAlign, type CompareWindows } from './compare'

interface Carriage { name: string; people: number; seats: number }
interface Service { shd_rec_no: number; service_date: string; day: string; departure_time: string; service_description: string; service_type: string; season: string; individual_seats: number; coupes_sold: number; exclusivity_seats: number; people_incl: number; people_excl: number; capacity: number; pct_full_excl: number; pct_full_incl: number; carriages: Carriage[] }
interface Totals { label: string; from: string; to: string; services: number; people_excl: number; people_incl: number; capacity: number; coupes: number; pct_full_excl: number }
interface FoodLine { course: string; course_seq: number; item: string; qty: number; bookings: number }
interface FoodBlock { label: string; from: string; to: string; lines: FoodLine[]; meals: number; bookings: number }
interface Payload { from: string; to: string; windows: CompareWindows | null; last_refresh: string | null; services: Service[]; totals: Totals[]; food: FoodBlock[]; food_meta: { from: string | null; to: string | null; refreshed_at: string | null }; last_season: { services: Service[]; totals: Totals[] } | null }
interface LayoutTable { carriage: string; table_name: string; kind: string; seats: number; aisle_side: string; order_seq: number }
interface SvcBooking { booking_ref: number; account_name: string; carriages: string[]; seat_refs: string; coupe_refs: string; individual_seats: number; coupes: number; people: number; exclusivity_seats: number; has_dietary: boolean; menu: FoodLine[] }
interface SvcDetail { service: { shd_rec_no: number; service_date: string; day: string; departure_time: string; service_description: string; service_type: string; individual_seats: number; coupes_sold: number; exclusivity_seats: number; people_incl: number; people_excl: number; capacity: number }; carriages: { id: string; name: string; people: number; seats: number; tables: LayoutTable[] }[]; bookings: SvcBooking[]; food: FoodLine[] }
interface RefreshStatus { in_flight: boolean; latest: { status: string; requested_by: string; requested_at: string | null; completed_at: string | null; message: string | null } | null; last_refresh: string | null; next_allowed_at: string; can_request: boolean; error?: string }

const GREEN = '#0b3d2e'
const num = (n: number) => n.toLocaleString('en-GB')
const pct = (n: number) => `${Math.round(n * 100)}%`
const dmy = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })
const hm = (iso: string | null) => (iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' }) : '—')
const TH: CSSProperties = { textAlign: 'left', padding: '5px 8px', color: '#6b6b6b', fontWeight: 600, fontSize: '0.72rem', borderBottom: '1px solid #e5e1d6', whiteSpace: 'nowrap' }
const TD: CSSProperties = { padding: '5px 8px', fontSize: '0.82rem', borderBottom: '1px solid #f0ece1', verticalAlign: 'top' }
const R: CSSProperties = { textAlign: 'right' }
const btn: CSSProperties = { font: 'inherit', fontSize: '0.82rem', fontWeight: 600, padding: '0.4rem 0.8rem', borderRadius: 4, cursor: 'pointer', border: `1px solid ${GREEN}`, background: '#fff', color: GREEN, textDecoration: 'none' }

// Table letters a booking's individual seats touch: "F1, F2, H1" -> [F, H]
function tableLetters(seatRefs: string): string[] {
  const set = new Set<string>()
  for (const raw of (seatRefs || '').split(',')) { const m = raw.trim().match(/^[A-Za-z]+/); if (m) set.add(m[0].toUpperCase()) }
  return Array.from(set)
}

/** The carriage plan: tables either side of the aisle in layout order, each with its party. */
function CarriagePlan({ detail }: { detail: SvcDetail }) {
  const [active, setActive] = useState(0)
  const carriages = detail.carriages
  const coach = carriages[Math.min(active, Math.max(0, carriages.length - 1))]
  const placed = useMemo(() => {
    const out = new Map<string, SvcBooking[]>() // `${carriage}|${table}` -> bookings
    const used = new Set<number>()
    for (const c of carriages) {
      const here = detail.bookings.filter((b) => b.booking_ref > 0 && b.carriages.includes(c.id))
      const coupeQueue = here.filter((b) => b.coupes > 0 && b.individual_seats === 0 && tableLetters(b.seat_refs).length === 0)
      let ci = 0
      for (const t of c.tables) {
        const letter = t.table_name.toUpperCase()
        let occ: SvcBooking[] = []
        if (t.kind === 'coupe') {
          const byLetter = here.filter((b) => b.coupes > 0 && tableLetters(b.seat_refs).includes(letter))
          occ = byLetter.length ? byLetter : coupeQueue[ci] ? [coupeQueue[ci++]] : []
        } else {
          occ = here.filter((b) => tableLetters(b.seat_refs).includes(letter))
        }
        occ.forEach((b) => used.add(b.booking_ref))
        out.set(`${c.id}|${t.table_name}`, occ)
      }
    }
    return { out, unplaced: detail.bookings.filter((b) => b.booking_ref > 0 && !used.has(b.booking_ref)) }
  }, [carriages, detail.bookings])
  if (!carriages.length) return <p style={{ color: '#6b6b6b' }}>No carriages are on this service yet.</p>
  const sideA = coach.tables.filter((t) => t.aisle_side === 'A')
  const sideB = coach.tables.filter((t) => t.aisle_side !== 'A')
  const card = (t: LayoutTable) => {
    const occ = placed.out.get(`${coach.id}|${t.table_name}`) ?? []
    const party = occ.reduce((a, b) => a + b.people, 0)
    const booked = occ.length > 0
    return (
      <div key={t.table_name} className={`pp-table${booked ? ' pp-table--booked' : ''}${t.kind === 'coupe' ? ' pp-table--coupe' : ''}`} title={occ.map((b) => `${b.account_name} · ${b.people} · ${b.seat_refs || b.coupe_refs}`).join('\n')}>
        <div className="pp-table__row"><span className="pp-table__letter">{t.table_name}</span><span className="pp-table__kind">{t.kind === 'coupe' ? 'Coupe' : `${t.seats} seats`}</span></div>
        <div className="pp-table__name">{booked ? occ.map((b) => b.account_name).join(' · ') : 'Available'}</div>
        <div className="pp-table__dots">{Array.from({ length: Math.max(t.seats, party) }, (_, k) => <span key={k} className={k < party ? 'on' : ''} />)}</div>
        <div className="pp-table__party">{booked ? `${party} of ${t.seats}` : ''}{occ.some((b) => b.has_dietary) ? ' · dietary note' : ''}</div>
      </div>
    )
  }
  return (
    <div className="pp-plan">
      <div className="pp-plan__tabs">
        {carriages.map((c, i) => <button key={c.id} type="button" className={`pp-plan__tab${i === active ? ' on' : ''}`} onClick={() => setActive(i)}>{c.name}<small>{c.people} people</small></button>)}
      </div>
      <div className="pp-coach">
        <div className="pp-coach__side">{sideA.map(card)}</div>
        <div className="pp-coach__aisle">aisle · direction of travel →</div>
        <div className="pp-coach__side">{sideB.map(card)}</div>
      </div>
      {placed.unplaced.length > 0 && (
        <p className="pp-note">Not placed on the plan ({placed.unplaced.length}): {placed.unplaced.map((b) => `${b.account_name} (${b.people}${b.carriages.length ? ', ' + b.carriages.join(' & ') : ''}${b.seat_refs ? ', ' + b.seat_refs : b.coupe_refs ? ', coupe ' + b.coupe_refs : ''})`).join('; ')}.</p>
      )}
    </div>
  )
}

function ServiceModal({ shd, onClose }: { shd: number; onClose: () => void }) {
  const [d, setD] = useState<SvcDetail | null>(null)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    fetch(`/api/dashboards/pullman/people/service?shd=${shd}`).then(async (r) => { if (!r.ok) throw new Error(`returned ${r.status}`); return r.json() as Promise<SvcDetail> }).then((j) => { if (live) setD(j) }).catch((e) => { if (live) setErr(String(e)) })
    return () => { live = false }
  }, [shd])
  useEffect(() => { const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }; window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k) }, [onClose])
  return (
    <div className="pp-modal__backdrop" onClick={onClose} role="presentation">
      <div className="pp-modal" role="dialog" aria-modal="true" aria-label="Service detail" onClick={(e) => e.stopPropagation()}>
        <div className="pp-modal__head">
          <div>
            {d ? <><h3>{d.service.day} {dmy(d.service.service_date)} · {d.service.departure_time} · {d.service.service_description}</h3>
              <p>{num(d.service.people_excl)} people travelling ({num(d.service.people_incl)} incl. {d.service.exclusivity_seats} exclusivity seats) · {d.service.coupes_sold} coupes · capacity {d.service.capacity} · {d.bookings.filter((b) => b.booking_ref > 0).length} bookings</p></> : <h3>Loading…</h3>}
          </div>
          <button type="button" className="pp-modal__close" onClick={onClose} aria-label="Close">×</button>
        </div>
        {err && <p style={{ color: '#a33' }}>Couldn&apos;t load: {err}</p>}
        {d && (
          <div className="pp-modal__body">
            <CarriagePlan detail={d} />
            <div className="pp-modal__cols">
              <div>
                <h4>Who is where</h4>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead><tr><th style={TH}>Booking</th><th style={TH}>Name</th><th style={TH}>Carriage</th><th style={TH}>Tables</th><th style={TH}>Seats</th><th style={{ ...TH, ...R }}>People</th></tr></thead>
                  <tbody>{d.bookings.map((b) => (
                    <tr key={`${b.booking_ref}-${b.seat_refs}`}>
                      <td style={TD}>{b.booking_ref || '—'}</td><td style={TD}>{b.account_name}{b.has_dietary ? <span className="pp-pill">dietary</span> : null}{b.exclusivity_seats ? <span className="pp-pill pp-pill--grey">{b.exclusivity_seats} exclusivity</span> : null}</td>
                      <td style={TD}>{b.carriages.join(' & ')}</td><td style={TD}>{tableLetters(b.seat_refs).join(', ')}{b.coupe_refs ? (tableLetters(b.seat_refs).length ? ' · ' : '') + 'coupe' : ''}</td>
                      <td style={{ ...TD, fontSize: '0.75rem', color: '#555' }}>{b.seat_refs}{b.coupe_refs ? ` ${b.coupe_refs}` : ''}</td><td style={{ ...TD, ...R, fontWeight: 600 }}>{b.people}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
              <div>
                <h4>Food on this service</h4>
                {d.food.length === 0 ? <p className="pp-note">No pre-ordered courses held for this service yet.</p> : (
                  <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                    <thead><tr><th style={TH}>Course</th><th style={TH}>Item</th><th style={{ ...TH, ...R }}>Qty</th><th style={{ ...TH, ...R }}>Bookings</th></tr></thead>
                    <tbody>{d.food.map((f) => <tr key={`${f.course_seq}|${f.item}`}><td style={TD}>{f.course}</td><td style={TD}>{f.item}</td><td style={{ ...TD, ...R, fontWeight: 600 }}>{f.qty}</td><td style={{ ...TD, ...R }}>{f.bookings}</td></tr>)}</tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export function PullmanPeople({ from, to, compare, align }: { from: string | null; to: string | null; compare: boolean; align: CompareAlign }) {
  const [data, setData] = useState<Payload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [excl, setExcl] = useState(true)
  const [open, setOpen] = useState<number | null>(null)
  const [foodPeriod, setFoodPeriod] = useState(1)
  const [nonce, setNonce] = useState(0)
  const [status, setStatus] = useState<RefreshStatus | null>(null)
  const [posting, setPosting] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)

  useEffect(() => {
    if (!from || !to) return
    let live = true
    setLoading(true)
    const qs = new URLSearchParams({ from, to }); if (compare) { qs.set('compare', 'ly'); qs.set('align', align) }
    fetch(`/api/dashboards/pullman/people?${qs}`).then(async (r) => { if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.error || `returned ${r.status}`) } return r.json() as Promise<Payload> })
      .then((j) => { if (live) { setData(j); setError(null) } }).catch((e) => { if (live) setError(String((e as Error).message ?? e)) }).finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [from, to, compare, align, nonce])

  // Shared Update-now status: polled so a refresh anyone starts shows for everyone, and the
  // page reloads itself when the Pullman data advances.
  const poll = useCallback(async () => {
    try {
      const r = await fetch('/api/dashboards/pullman/people/refresh'); if (!r.ok) return
      const s = (await r.json()) as RefreshStatus
      setStatus((prev) => { if (prev?.last_refresh && s.last_refresh && s.last_refresh > prev.last_refresh) setNonce((n) => n + 1); return s })
    } catch { /* next tick */ }
  }, [])
  useEffect(() => { void poll(); const t = window.setInterval(() => void poll(), 10_000); return () => window.clearInterval(t) }, [poll])
  const onUpdate = async () => {
    if (posting) return
    setPosting(true); setMsg(null)
    try {
      const r = await fetch('/api/dashboards/pullman/people/refresh', { method: 'POST', headers: { Accept: 'application/json' } })
      const b = (await r.json().catch(() => ({}))) as RefreshStatus
      if (r.status === 429) setMsg(b.error ?? 'Updated less than an hour ago.')
      else if (!r.ok && r.status !== 202) setMsg(b.error ?? `Request failed (${r.status})`)
      else setMsg(b.in_flight || r.status === 202 ? 'Update requested — the Pullman data refreshes in about a minute.' : null)
      await poll()
    } catch (e) { setMsg(String((e as Error).message ?? e)) } finally { setPosting(false) }
  }

  const people = (s: { people_excl: number; people_incl: number }) => (excl ? s.people_excl : s.people_incl)
  const lyTotals = data?.last_season?.totals ?? null
  const lyLabel = data?.windows ? `Last season (${shortSeasonLabel(data.windows.ly_season)})` : 'Last season'
  const food = data?.food[foodPeriod]
  const courses = useMemo(() => { const m = new Map<string, FoodLine[]>(); for (const l of food?.lines ?? []) { const a = m.get(l.course) ?? []; a.push(l); m.set(l.course, a) } return Array.from(m.entries()) }, [food])

  if (!from || !to) return <p>Pick a date range above.</p>
  return (
    <div className="pp">
      <div className="pp-bar">
        <label className="pp-toggle"><input type="checkbox" checked={excl} onChange={(e) => setExcl(e.target.checked)} /> Exclude exclusivity seats <span title="A seat bought to keep a table private — taken, but no diner. The kitchen and menu cards count people without them.">ⓘ</span></label>
        <span style={{ flex: 1 }} />
        <span className="pp-asat">Pullman data as at {hm(status?.last_refresh ?? data?.last_refresh ?? null)}{status?.in_flight ? ' · updating now…' : ''}</span>
        <button type="button" style={{ ...btn, background: GREEN, color: '#fff' }} disabled={posting || !!status?.in_flight || status?.can_request === false} onClick={() => void onUpdate()}
          title={status?.can_request === false ? `One update an hour. Next allowed at ${hm(status.next_allowed_at)}` : 'Fetch the latest Pullman seat map from MERAC now (one update an hour, shared by everyone)'}>
          {status?.in_flight ? 'Updating…' : 'Update now'}
        </button>
        <a href={`/api/dashboards/pullman/people/xlsx?from=${from}&to=${to}`} style={btn} download>Download XLS</a>
        <a href={`/api/dashboards/pullman/people/pdf?from=${from}&to=${to}`} style={btn} download>Download PDF</a>
      </div>
      {msg && <p className="pp-note">{msg}</p>}
      {loading && !data && <p>Loading…</p>}
      {error && <p style={{ color: '#a33' }}>Couldn&apos;t load: {error}</p>}
      {data && (
        <>
          <section className="kpi-section">
            <header className="kpi-section__head"><span className="kpi-section__label">People booked{excl ? '' : ' (incl. exclusivity seats)'}</span></header>
            <div className="kpi-bar">
              {data.totals.map((t, i) => {
                const ly = lyTotals?.[i]
                return (
                  <div key={t.label} className="kpi-card" title={`${windowLabel(t.from, t.to)} · ${t.services} services · capacity ${num(t.capacity)}`}>
                    <div className="kpi-card__label">{t.label}</div>
                    <div className="kpi-card__value">{loading ? '…' : num(people(t))}</div>
                    <div className="pp-kpi__sub">{t.services} services · {pct(t.capacity > 0 ? people(t) / t.capacity : 0)} full{excl ? ` · ${num(t.people_incl)} incl.` : ''}</div>
                    {ly && <div className="pp-kpi__ly" style={{ color: LY_GREY }}>{lyLabel}: {num(people(ly))} · {deltaLabel(people(t), people(ly), num)} · {ly.services} services</div>}
                  </div>
                )
              })}
            </div>
          </section>

          <section className="pp-card">
            <h3>Services {windowLabel(from, to)} <small>{data.services.length} services · {num(data.services.reduce((a, s) => a + people(s), 0))} people</small></h3>
            <div className="pp-scroll">
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead><tr><th style={TH}>Date</th><th style={TH}>Time</th><th style={TH}>Service</th><th style={{ ...TH, ...R }}>People</th>{excl && <th style={{ ...TH, ...R }}>Excl. seats</th>}<th style={{ ...TH, ...R }}>Coupes</th><th style={{ ...TH, ...R }}>Capacity</th><th style={{ ...TH, ...R }}>Full</th><th style={TH}>By carriage</th><th style={TH}></th></tr></thead>
                <tbody>{data.services.map((s) => (
                  <tr key={s.shd_rec_no} className="pp-row" onClick={() => setOpen(s.shd_rec_no)}>
                    <td style={{ ...TD, whiteSpace: 'nowrap' }}>{s.day} {dmy(s.service_date)}</td><td style={TD}>{s.departure_time}</td><td style={TD}>{s.service_description}</td>
                    <td style={{ ...TD, ...R, fontWeight: 700 }}>{num(people(s))}</td>{excl && <td style={{ ...TD, ...R, color: '#8a8577' }}>{s.exclusivity_seats || ''}</td>}
                    <td style={{ ...TD, ...R }}>{s.coupes_sold}</td><td style={{ ...TD, ...R }}>{s.capacity}</td><td style={{ ...TD, ...R }}>{pct(excl ? s.pct_full_excl : s.pct_full_incl)}</td>
                    <td style={{ ...TD, fontSize: '0.75rem', color: '#555' }}>{s.carriages.map((c) => `${c.name} ${c.people}`).join(' · ')}</td>
                    <td style={{ ...TD, color: GREEN, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Who is where ▸</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            {data.last_season && data.windows && <p className="pp-note">{lyLabel}, {windowLabel(data.windows.ly_from, data.windows.ly_to)}: {data.last_season.services.length} services, {num(data.last_season.services.reduce((a, s) => a + people(s), 0))} people.</p>}
          </section>

          <section className="pp-card">
            <h3>Food summary <small>meals pre-ordered, counted once per booking</small></h3>
            <div className="pp-plan__tabs">{data.food.map((f, i) => <button key={f.label} type="button" className={`pp-plan__tab${i === foodPeriod ? ' on' : ''}`} onClick={() => setFoodPeriod(i)}>{f.label}<small>{num(f.meals)} meals</small></button>)}</div>
            {food && (courses.length === 0 ? <p className="pp-note">No pre-ordered courses held for {windowLabel(food.from, food.to)}.</p> : (
              <div className="pp-food">
                {courses.map(([course, lines]) => (
                  <table key={course} style={{ borderCollapse: 'collapse', width: '100%' }}>
                    <caption>{course} <small>{num(lines.reduce((a, l) => a + l.qty, 0))}</small></caption>
                    <tbody>{lines.map((l) => <tr key={l.item}><td style={TD}>{l.item}</td><td style={{ ...TD, ...R, fontWeight: 600 }}>{num(l.qty)}</td></tr>)}</tbody>
                  </table>
                ))}
              </div>
            ))}
            <p className="pp-note">The dining bookings held cover {data.food_meta.from ? dmy(data.food_meta.from) : '—'} to {data.food_meta.to ? dmy(data.food_meta.to) : '—'} (refreshed {hm(data.food_meta.refreshed_at)}), so a period starting earlier shows only that part. Drinks pre-orders are listed under "Drinks &amp; extras" and are not counted as meals.</p>
          </section>
        </>
      )}
      {open !== null && <ServiceModal shd={open} onClose={() => setOpen(null)} />}
    </div>
  )
}
